import { memo, useMemo, useState } from "react";
import {
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  finite,
  formatMm as mm,
  levelDomain,
  levelTicks,
  sgtTime as sgt,
  timeOf,
} from "utils/radarDiagnostics";
import {
  ASSUMED_STEP_MM,
  WIDTH_DROP_DB,
  adcStats,
  beatSpectrum,
  clipFrom,
  clipText,
  compareSpectra,
  comparisonRows,
} from "utils/radarSignal";
import { captureArrival, hasSpectrum } from "utils/radarStream";
import { LIVE_COLORS, spectrumDomain } from "./RadarSpectrum";
import { More } from "./radarUi";

// Radar live: the beat signal (the 512 ADC samples before the radar's own
// FFT) and our FFT of it beside the radar's spectrum. Node 1's radar
// computes its spectrum as |FFT(samples - mean)| with no window and no
// padding, so our rectangular FFT lies on it; ours with a Hann window and ×8
// zero-padding resolves finer. Its receiver clips on a strong close echo.
export const BEAT_COLORS = {
  beat: "#2c68cf",
  rect: "#7b5cc4",
  clip: "#f4c56a",
};
const FLOOR_DB = -90;
// The unclipped part needs this many samples for an FFT worth showing.
const MIN_TAIL = 64;

// The beat signal: one capture's samples, or several overlaid (thin lines,
// a burst's captures, named by `names`), with the clipped part (`shade`:
// [first, last]) shaded.
export const Waveform = memo(function Waveform({
  series,
  names = null,
  shade = null,
}) {
  const length = series.reduce((top, s) => Math.max(top, s.length), 0);
  const rows = useMemo(
    () =>
      Array.from({ length }, (_, i) => {
        const row = { i };
        series.forEach((s, k) => {
          row[`v${k}`] = Number.isFinite(s[i]) ? s[i] : null;
        });
        return row;
      }),
    [series, length],
  );
  const many = series.length > 1;
  return (
    <div className="field-stream-beat-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={rows}
          margin={{ top: 8, right: 10, bottom: 0, left: 0 }}
        >
          <CartesianGrid stroke="#e8eff1" vertical={false} />
          <XAxis
            dataKey="i"
            type="number"
            domain={[0, Math.max(1, length - 1)]}
            ticks={[0, 128, 256, 384, length - 1].filter(
              (t, i, all) => t >= 0 && all.indexOf(t) === i,
            )}
            tick={{ fontSize: 10, fill: "#6d8490" }}
          />
          <YAxis
            tickFormatter={(v) => Math.round(v).toLocaleString()}
            tick={{ fontSize: 10, fill: "#6d8490" }}
            width={56}
          />
          {shade && (
            <ReferenceArea
              x1={shade[0]}
              x2={shade[1]}
              fill={BEAT_COLORS.clip}
              fillOpacity={0.35}
              ifOverflow="hidden"
            />
          )}
          <ReferenceLine y={0} stroke="#c9d6db" />
          <Tooltip
            labelFormatter={(v) => `Sample ${v}`}
            formatter={(value, name) => [
              `${Number(value).toLocaleString()} counts`,
              name,
            ]}
          />
          {series.map((_, k) => (
            <Line
              key={k}
              dataKey={`v${k}`}
              name={names?.[k] || (many ? `Capture ${k + 1}` : "ADC")}
              stroke={BEAT_COLORS.beat}
              strokeWidth={many ? 0.8 : 1.1}
              strokeOpacity={many ? 0.55 : 1}
              dot={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
});

// Our FFT (Hann, ×8) beside the radar's spectrum and our rectangular FFT;
// with `thin` captures (a burst), each capture's FFT as a thin line (o0, o1,
// ...) and their mean as ours.
export const Comparison = memo(function Comparison({
  rows,
  domain,
  hasRadar,
  thin = 0,
}) {
  const values = rows.flatMap((r) => [r.ours, r.radar, r.rect]);
  const [lo, hi] = levelDomain(values);
  const yDomain = [
    Math.max(Number.isFinite(lo) ? lo : FLOOR_DB, FLOOR_DB),
    Number.isFinite(hi) ? hi : 0,
  ];
  const hasRect = rows.some((r) => r.rect !== undefined && r.rect !== null);
  return (
    <div className="field-stream-beat-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={rows}
          margin={{ top: 8, right: 10, bottom: 0, left: 0 }}
        >
          <CartesianGrid stroke="#e8eff1" vertical={false} />
          <XAxis
            dataKey="d"
            type="number"
            domain={domain}
            allowDataOverflow
            tickFormatter={(v) => v.toLocaleString()}
            tick={{ fontSize: 10, fill: "#6d8490" }}
          />
          <YAxis
            domain={yDomain}
            ticks={levelTicks(yDomain)}
            allowDataOverflow
            tickFormatter={(v) => Math.round(v)}
            tick={{ fontSize: 10, fill: "#6d8490" }}
            unit=" dB"
            width={62}
          />
          <Tooltip
            labelFormatter={(v) => `${Math.round(v).toLocaleString()} mm`}
            formatter={(value, name) => [
              value === null || value === undefined
                ? "—"
                : `${Number(value).toFixed(1)} dB`,
              name,
            ]}
          />
          {Array.from({ length: thin }, (_, k) => (
            <Line
              key={`o${k}`}
              dataKey={`o${k}`}
              name={`Capture ${k + 1} (ours)`}
              stroke={LIVE_COLORS.ours}
              strokeWidth={0.8}
              strokeOpacity={0.4}
              dot={false}
              activeDot={false}
              isAnimationActive={false}
              tooltipType="none"
            />
          ))}
          {hasRadar && (
            <Line
              dataKey="radar"
              name={thin ? "Radar's mean spectrum" : "Radar's spectrum"}
              stroke={LIVE_COLORS.spectrum}
              strokeWidth={1.6}
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
          )}
          {hasRect && (
            <Line
              dataKey="rect"
              name={
                thin ? "Our FFT, rectangular, mean" : "Our FFT, rectangular"
              }
              stroke={BEAT_COLORS.rect}
              strokeWidth={1.4}
              strokeDasharray="4 3"
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
          )}
          <Line
            dataKey="ours"
            name={thin ? "Our FFT, Hann ×8, mean" : "Our FFT, Hann ×8"}
            stroke={LIVE_COLORS.ours}
            strokeWidth={thin ? 2.2 : 1.6}
            dot={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
});

// The key under a comparison: the radar's spectrum, our rectangular FFT and
// our Hann ×8 FFT (of the unclipped part only, when so).
export function ComparisonKey({ hasRadar, tail = null, thin = false }) {
  const key = (color, dashed = false) => (
    <span
      className={`field-radar-key ${dashed ? "is-dashed" : "is-solid"}`}
      style={{ "--key": color }}
      aria-hidden="true"
    />
  );
  return (
    <ul className="field-radar-legend" aria-label="Beat signal key">
      {hasRadar && (
        <li>
          {key(LIVE_COLORS.spectrum)}
          {thin ? "Radar's mean spectrum" : "Radar's spectrum"}
        </li>
      )}
      <li>
        {key(BEAT_COLORS.rect, true)}
        Our FFT, rectangular (same as the radar's)
      </li>
      <li>
        {key(LIVE_COLORS.ours)}
        {tail
          ? `Our FFT, Hann ×8 zero-padded, unclipped part only (${tail}; lower resolution)`
          : "Our FFT, Hann ×8 zero-padded (finer)"}
      </li>
    </ul>
  );
}

// What the comparison's differences mean.
export function ComparisonNotes() {
  return (
    <details className="field-radar-details">
      <summary>What differences mean</summary>
      <ul className="field-stream-notes">
        <li>
          Every curve is scaled to 0 dB at its own strongest echo beyond the
          near field: compare shapes and positions, not levels.
        </li>
        <li>
          The radar computes its spectrum as |FFT(samples − mean)| with no
          window and no padding (on node 1, r = 0.998 in dB, the same peak bin),
          so our rectangular curve should lie on it. Where it does not, the
          samples are not that spectrum's input.
        </li>
        <li>
          Ours with a Hann window and ×8 zero-padding has 8 points per radar bin
          and lower side lobes: its peak gives a finer position, less biased by
          nearby echoes than a parabola on the radar's unwindowed spectrum.
        </li>
        <li>
          Distance = bin × range step − calibration offset, with no index shift
          (confirmed on node 1).
        </li>
        <li>
          A clipping receiver adds harmonics at whole multiples of a strong
          echo's distance. The unclipped part alone has fewer samples, so its
          peaks are wider (lower resolution).
        </li>
        <li>
          Near bins: we remove the DC offset before the FFT. Differences that
          change from capture to capture are noise; ones that repeat are
          processing.
        </li>
      </ul>
    </details>
  );
}

// "Samples 203-511" for the part after the clipping, or null when too short.
export function unclippedTail(clip, n) {
  if (!clip?.clipped) return null;
  const from = clip.last + 1;
  return n - from >= MIN_TAIL ? from : null;
}

const bins2 = (v) => `${v.toFixed(2)} bins`;
const sentence = (text) => text.charAt(0).toUpperCase() + text.slice(1);
const apart = (v) => {
  const r = Math.round(v * 10) / 10;
  return `${r > 0 ? "+" : r < 0 ? "−" : "±"}${mm(Math.abs(r), 1)}`;
};
const below = (v) =>
  v === null || v === undefined ? "—" : `${v.toFixed(1)} dB`;

// Why a capture has no beat signal, as far as it tells. `modes`: the server
// accepts the raw output mode (set in Radar rich data on Node Control).
function noBeatText(capture, modes) {
  if (capture.fromWaterfall)
    return "The waterfall keeps only the spectrum, and this capture is no longer stored.";
  const a = captureArrival(capture);
  const packets =
    a.adc === 1 ? "a beat-signal packet" : `${a.adc} beat-signal packets`;
  if (a.counted && a.adc > 0)
    return `No beat signal stored with this capture, although the node counted ${packets}${a.mode !== null ? ` in raw output mode ${a.mode}` : ""}. It keeps them only while Beat signal is ticked.`;
  if (a.counted && a.mode !== null)
    return `No beat signal in this capture: in raw output mode ${a.mode} the radar sent none.${
      a.mode === 1 || a.mode === 4
        ? ` Mode ${a.mode} sends only the spectrum; mode 2 sends the beat signal, without the spectrum.${modes ? " Choose it, or alternate the two, in Radar rich data on Node Control." : ""}`
        : `${a.mode === 3 || a.mode === 5 ? ` Mode ${a.mode} sends nothing on this radar.` : ""}${modes ? " Try another raw output mode in Radar rich data on Node Control." : ""}`
    }`;
  return "No beat signal in this capture. It needs Beat signal ticked (512 ADC samples, about 1.4 kB more per capture) and a raw output mode that sends it (mode 2 on node 1's radar): mode 1, the only one before firmware rtct.55, sends only the spectrum.";
}

// `capture` and `detail`: the capture whose beat signal is shown (with
// alternation, a neighbour of the capture shown above); `spectrum`: the
// capture whose radar spectrum to set beside it (the capture itself when it
// has one).
export default function RadarBeatSignal({
  capture,
  detail,
  range = "near",
  modes = false,
  spectrum = null,
}) {
  const [unclippedOnly, setUnclippedOnly] = useState(false);
  const samples = useMemo(
    () =>
      Array.isArray(detail?.data?.adc)
        ? detail.data.adc.map((v) => Number(v)).filter(Number.isFinite)
        : null,
    [detail],
  );
  const cal =
    finite(capture?.cal_mm) ?? finite(detail?.data?.context?.cfg?.cal_mm) ?? 0;
  const beside =
    spectrum && spectrum !== capture && hasSpectrum(spectrum) ? spectrum : null;
  // A spectrum carries the radar's range step; a beat signal alone carries
  // the server's assumed 30 mm (range_step_source "assumed"), about 5 % short
  // of node 2's 31.5 mm: its neighbour's spectrum comes first.
  const step =
    (hasSpectrum(capture) ? finite(capture?.step_mm) : null) ||
    finite(beside?.step_mm) ||
    (detail?.data?.range_step_source === "range_res"
      ? finite(detail?.data?.range_step_mm)
      : null) ||
    finite(capture?.step_mm) ||
    finite(detail?.data?.range_step_mm) ||
    ASSUMED_STEP_MM;
  const radarDb = useMemo(() => {
    if (hasSpectrum(capture)) return capture.db;
    if (beside) return beside.db;
    const amplitude = detail?.data?.amplitude;
    const fromDetail = Array.isArray(amplitude)
      ? amplitude.map((a) =>
          Number(a) > 0 ? 20 * Math.log10(Number(a)) : null,
        )
      : null;
    return hasSpectrum({ db: fromDetail }) ? fromDetail : null;
  }, [capture, beside, detail]);
  // The server's measure when it has one (clip), else ours, the same way.
  const clip = useMemo(
    () => (samples ? clipFrom(detail?.data, samples) : null),
    [detail, samples],
  );
  const tailFrom = samples ? unclippedTail(clip, samples.length) : null;
  const useTail = unclippedOnly && tailFrom !== null;
  const ours = useMemo(
    () =>
      samples
        ? beatSpectrum(samples, { pad: 8, from: useTail ? tailFrom : 0 })
        : null,
    [samples, useTail, tailFrom],
  );
  const rect = useMemo(
    () => (samples ? beatSpectrum(samples, { pad: 1, window: "rect" }) : null),
    [samples],
  );
  const stats = useMemo(() => (samples ? adcStats(samples) : null), [samples]);
  const series = useMemo(() => (samples ? [samples] : []), [samples]);
  const domain = useMemo(
    () => spectrumDomain(capture, range),
    [capture, range],
  );
  const rows = useMemo(
    () => comparisonRows(radarDb, ours, step, cal, domain, rect),
    [radarDb, ours, step, cal, domain, rect],
  );
  const compared = useMemo(
    () => compareSpectra(radarDb, ours),
    [radarDb, ours],
  );

  if (!capture) return null;
  if (!capture.has_adc)
    return (
      <div className="field-radar-empty">{noBeatText(capture, modes)}</div>
    );
  if (detail?.error)
    return (
      <div className="field-alert" role="alert">
        The beat signal could not load: {detail.error}
      </div>
    );
  if (!detail?.data)
    return <p className="field-caption">Loading the beat signal…</p>;
  if (!samples?.length || !ours)
    return (
      <div className="field-radar-empty">
        The stored capture holds no ADC samples, although the stream said it
        would.
      </div>
    );
  const toMm = (bin) => bin * step - cal;
  const { radar, ours: mine, delta } = compared;
  const apartS =
    beside && !hasSpectrum(capture)
      ? Math.abs((timeOf(beside.t) ?? 0) - (timeOf(capture.t) ?? 0)) / 1000
      : null;
  const tail = useTail ? `samples ${tailFrom}-${samples.length - 1}` : null;
  return (
    <div className="field-stream-beat">
      <div>
        <h4 className="field-stream-subhead">
          The {samples.length} samples
          {capture.t ? ` · ${sgt(capture.t)}` : ""}
        </h4>
        {clip?.clipped && (
          <p className="field-radar-pill is-warning" role="note">
            {sentence(clipText(clip))}
          </p>
        )}
        <Waveform
          series={series}
          shade={clip?.clipped ? [clip.first, clip.last] : null}
        />
        <p className="field-caption">
          {stats.min.toLocaleString()} to {stats.max.toLocaleString()} counts ·
          mean (DC offset) {stats.mean.toFixed(1)} · RMS {stats.rms.toFixed(1)}{" "}
          ·{" "}
          {stats.clipped
            ? `${stats.clipped} at the int16 limits`
            : "none at the int16 limits"}
          .
          {clip?.clipped
            ? ` The receiver clips: flat tops${clip.hi !== null && clip.lo !== null ? ` near ${clip.hi.toLocaleString()} and ${clip.lo.toLocaleString()} counts` : ""}, shaded over samples ${clip.first}-${clip.last}.`
            : ""}
        </p>
        <More label="How to read it" className="is-why">
          <p className="field-caption">
            Each echo adds a sine whose frequency is set by its distance.
            {clip?.clipped
              ? " Clipping is analog saturation on a strong close echo, not the int16 limits. It adds harmonics at whole multiples of a strong echo's distance: echoes at 2-4 times the strongest are marked as possible harmonics in our echo table."
              : ""}
          </p>
        </More>
        {clip?.clipped && (
          <label className="field-burst-toggle">
            <input
              type="checkbox"
              checked={useTail}
              disabled={tailFrom === null}
              onChange={(e) => setUnclippedOnly(e.target.checked)}
            />
            Unclipped part only
            <small>
              {tailFrom === null
                ? "Too few samples after the clipping for an FFT."
                : `Our Hann FFT over samples ${tailFrom}-${samples.length - 1}: lower resolution.`}
            </small>
          </label>
        )}
      </div>
      <div>
        <h4 className="field-stream-subhead">
          {radarDb
            ? beside && apartS !== null
              ? `Our FFT beside the radar's spectrum of ${sgt(beside.t)} (${Math.round(apartS)} s apart)`
              : "Our FFT beside the radar's spectrum"
            : "Our FFT (no radar spectrum to compare)"}
        </h4>
        <Comparison rows={rows} domain={domain} hasRadar={!!radarDb} />
        <ComparisonKey hasRadar={!!radarDb} tail={tail} />
        <p className="field-caption">
          {mine
            ? `Strongest echo beyond the near field: ours (Hann ×8) at ${mm(toMm(mine.bin), 1)} (bin ${mine.bin.toFixed(2)})` +
              (radar
                ? `, the radar's at ${mm(toMm(radar.bin), 1)} (bin ${radar.bin.toFixed(2)}): ${apart(delta * step)} apart. −${WIDTH_DROP_DB} dB width ${mine.width === null ? "—" : bins2(mine.width)} against ${radar.width === null ? "—" : bins2(radar.width)}; floor ${below(mine.floorBelow)} below the peak against ${below(radar.floorBelow)}.`
                : ". There is no radar spectrum to compare with.")
            : "Our FFT finds no echo beyond the near field."}
        </p>
        <ComparisonNotes />
      </div>
    </div>
  );
}
